import { COLORS } from '@btime/constants'
import { dropLast, path } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HorizontalLine } from '../../../../../../../components/shapes/Line'
import { SmallText, Title } from '../../../../../../../components/presentation/Text'
import { canRenderInputForType } from '../_helpers/hideInputForType'
import { useChecklistForm, useChecklistFormActions, useSelectedQuestion } from '../../../../_form/ChecklistFormHandler'
import FormItem from '../../../../../../../components/form/FormItem'
import InputWithInterpolations from '../../../../../../../packages/kiti-editor/components/input/InputWithInterpolations'
import Separator from '../../../../../../../packages/kiti/components/helpers/Separator'
import { useNumberFields } from '../../../../_load/FieldsLoader'
import {
  findChildren,
  formatQuestionsByOrigin,
  getCalculableQuestions,
  getQuestionsByType
} from '../../../../../../../utils/helpers/questions'
import { usePlaceTypes } from '../../../../../../../utils/helpers/placeTypes'

const CalcSection = () => {
  const { t } = useTranslation('editChecklist')
  const { handleSelectedQuestionChange } = useChecklistFormActions()
  const { selectedQuestionPath, ...form } = useChecklistForm() || {}
  const selectedQuestion = useSelectedQuestion()

  const shouldRender = canRenderInputForType(selectedQuestion.questionType.name)
  const shouldRenderCalc = shouldRender('calc')
  const fields = useNumberFields()

  const siblingsPath = dropLast(1, selectedQuestionPath)
  const parentPath = dropLast(3, selectedQuestionPath)
  const questions = path(siblingsPath, form) || []
  const parentQuestions = path(parentPath, form) || []

  const [types] = usePlaceTypes()

  const { randomIdAfterUndo } = useChecklistFormActions()

  if (!shouldRenderCalc) return false

  const allPlaces = types.map(item => {
    return {
      origin: item.key,
      questionType: 'place'
    }
  })

  const parentQuestionName = parentQuestions.filter(question => {
    return question.children.some(child => child.reference === selectedQuestion?.reference)
  })[0]?.name

  const parentCategories = findChildren(form.categories, ({ reference }) => reference === selectedQuestion?.reference)
  const questionCategory = parentCategories?.selectedCategory?.name ?? t('noCategoryDefined')

  const questionDescription = parentQuestionName ? `${questionCategory} - ${parentQuestionName}` : questionCategory

  const filteredPlaces = questions
    .filter(question => question.questionType?.name === 'place')
    .flatMap(question => question.data?.filter?.placeLevelIds || [])
    .reduce((uniqueTypes, level) => {
      const type = level.type
      if (!uniqueTypes.find(uniqueType => uniqueType.questionType === 'place' && uniqueType.origin === type)) {
        uniqueTypes.push({ questionType: 'place', origin: type })
      }
      return uniqueTypes
    }, [])

  const placeQuestions = filteredPlaces.length ? filteredPlaces : allPlaces

  const calculableQuestions = getCalculableQuestions(questions, selectedQuestion, questionDescription, questionCategory)

  const assetQuestions = getQuestionsByType(questions, selectedQuestion, 'asset')
  const formattedAssetQuestions = formatQuestionsByOrigin(
    assetQuestions,
    fields,
    'assets',
    questionDescription,
    questionCategory
  )

  const userQuestions = getQuestionsByType(questions, selectedQuestion, 'user')
  const formattedUserQuestions = formatQuestionsByOrigin(
    userQuestions,
    fields,
    'contracts',
    questionDescription,
    questionCategory
  )

  const formattedPlacesQuestions =
    placeQuestions
      .flatMap(question => {
        const allQuestions = getQuestionsByType(questions, selectedQuestion, question.questionType)
        const formattedQuestions = formatQuestionsByOrigin(
          allQuestions,
          fields,
          question.origin,
          questionDescription,
          questionCategory
        )
        return [formattedQuestions]
      })
      .flat() || []

  const formattedQuestions = calculableQuestions.concat(
    formattedAssetQuestions,
    formattedUserQuestions,
    formattedPlacesQuestions
  )

  return (
    <>
      <Separator weight={6} />
      <Title size="h6" color={COLORS.dark4}>
        {t('calculation')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={2} />

      <FormItem label={t('formula')} key={randomIdAfterUndo}>
        <InputWithInterpolations
          key={selectedQuestion.reference}
          rows={2}
          value={selectedQuestion.data?.calc}
          interpolations={formattedQuestions}
          onChange={v => !!v && handleSelectedQuestionChange('data', { ...selectedQuestion.data, calc: v })}
        />
        <SmallText color={COLORS.dark5}>
          {t('calcNote')}: <strong>1.234,56</strong>
        </SmallText>
        <SmallText color={COLORS.dark5}>{t('calcMinFieldsRequired')}</SmallText>
      </FormItem>
    </>
  )
}

export default CalcSection
